type FrontendLogLevel = 'debug' | 'info' | 'warn' | 'error'
type FrontendLogValue = unknown

const LOG_LEVEL_PRIORITY: Record<FrontendLogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40
}

const toBool = (value?: string): boolean => String(value).toLowerCase() === 'true'

const resolveFrontendLogLevel = (): FrontendLogLevel => {
  const silent = toBool(import.meta.env.VITE_LOG_SILENT)
  if (silent) return 'error'

  const explicitLevel = String(import.meta.env.VITE_LOG_LEVEL || '').toLowerCase()
  if (explicitLevel === 'debug' || explicitLevel === 'info' || explicitLevel === 'warn' || explicitLevel === 'error') {
    return explicitLevel
  }

  const legacyDebugEnabled = toBool(import.meta.env.VITE_FRONTEND_DEBUG_LOGS)
  if (legacyDebugEnabled && import.meta.env.DEV) {
    return 'debug'
  }

  return import.meta.env.DEV ? 'warn' : 'error'
}

const effectiveLogLevel = resolveFrontendLogLevel()
const shouldLog = (targetLevel: FrontendLogLevel): boolean =>
  LOG_LEVEL_PRIORITY[targetLevel] >= LOG_LEVEL_PRIORITY[effectiveLogLevel]

const SENSITIVE_KEY_RE =
  /(secret|token|password|authorization|bearer|jwt|credential|api_key|apikey|client_secret|x-client-secret|cookie)/i

const SENSITIVE_VALUE_REPLACERS: Array<[RegExp, string]> = [
  [/\b(Bearer)\s+[A-Za-z0-9\-._~+/]+=*/gi, '$1 ***'],
  [/\b(token|access_token|refresh_token|client_secret|password|senha|authorization|cookie)\s*[:=]\s*["']?[^"',\s]+["']?/gi, '$1=***']
]

const sanitizeStringForLog = (value: string): string => {
  let sanitized = String(value)
  for (const [pattern, replacement] of SENSITIVE_VALUE_REPLACERS) {
    sanitized = sanitized.replace(pattern, replacement)
  }
  if (sanitized.length > 400) {
    return `${sanitized.slice(0, 180)}...[truncated,len=${sanitized.length}]`
  }
  return sanitized
}

const sanitizeLogArg = (value: FrontendLogValue, depth = 0): FrontendLogValue => {
  if (depth > 5) return '[max-depth]'
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return sanitizeStringForLog(value)
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => sanitizeLogArg(item, depth + 1))
  }

  const obj = value as Record<string, unknown>
  const output: Record<string, unknown> = {}
  for (const [key, currentValue] of Object.entries(obj).slice(0, 80)) {
    if (SENSITIVE_KEY_RE.test(key)) {
      output[key] = currentValue == null ? currentValue : '***'
      continue
    }
    output[key] = sanitizeLogArg(currentValue, depth + 1)
  }
  return output
}

const sanitizeArgs = (args: unknown[]): unknown[] => args.map((arg) => sanitizeLogArg(arg))

export const logger = {
  debug: (...args: unknown[]) => {
    if (shouldLog('debug')) console.debug(...sanitizeArgs(args))
  },
  info: (...args: unknown[]) => {
    if (shouldLog('info')) console.info(...sanitizeArgs(args))
  },
  warn: (...args: unknown[]) => {
    if (shouldLog('warn')) console.warn(...sanitizeArgs(args))
  },
  error: (...args: unknown[]) => {
    if (shouldLog('error')) console.error(...sanitizeArgs(args))
  },
  getEffectiveLevel: (): FrontendLogLevel => effectiveLogLevel
}

export type { FrontendLogLevel }
